import { createEffect, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import type { EditTarget } from '../lib/agent-config-editor';
import type { ApiTemplate, TemplateWrite } from '../lib/agent-templates';
import { createTemplateForm } from '../lib/template-form';
import { Modal } from './Modal';
import { TemplateFields } from './TemplateFields';
import { Button, buttonClass } from './ui/Button';

// Create / edit an agent template. Shell only — `createTemplateForm` holds the
// fields, <TemplateFields/> paints them, and the route owns the PUT.
//
// ONE caller-side check: whether the invocation box holds JSON, which has to be
// decided before there is a request to send. Everything else — the compatible-
// backend/base_url pairing, every bound, the scope gate — is the API's answer and
// renders inline from its `detail`, so all three doors refuse alike.
//
// Always MOUNTED, driven by `target`: unmounting on close would cut the
// dialog's exit animation, so the fields re-seed from the target instead —
// that is what makes "edit template B" show B rather than what A left behind.

export function TemplateFormModal(props: {
  /** The editor's target: a row to edit, `'new'` for a create, null when closed. */
  readonly target: EditTarget<ApiTemplate>;
  readonly pending: boolean;
  /** The API's refusal, shown inline. */
  readonly error: string | null;
  readonly onSave: (name: string, body: TemplateWrite) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const form = createTemplateForm(t('account_templates.invalid_json'));
  const template = (): ApiTemplate | null =>
    props.target === null || props.target === 'new' ? null : props.target;
  const editing = (): boolean => template() !== null;

  // Seeding local field state from an incoming prop is a legitimate effect: it
  // initializes on open, it does not mirror derived state.
  createEffect(
    on(
      () => props.target,
      () => form.seed(template()),
    ),
  );

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!form.valid() || props.pending) return;
    props.onSave(form.name().trim(), form.body());
  };

  return (
    <Modal
      open={props.target !== null}
      size="lg"
      title={t(editing() ? 'account_templates.edit_title' : 'account_templates.add_title')}
      desc={t('account_templates.form_desc')}
      onClose={props.onClose}
    >
      <form onSubmit={submit}>
        <TemplateFields form={form} editing={editing()} />

        <Show when={props.error}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('account_templates.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!form.valid() || props.pending}>
            {t(props.pending ? 'account_templates.saving' : 'account_templates.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
